// #region Imports
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FeeScheduleCategoryMaskedCurrency,
  Masked,
  MaskedFeeSchedule
} from "work/masked-fields";
import FeeScheduleCategory from "../fee-schedule-category";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import Tooltip from "@mui/material/Tooltip";
import ListItem from "@mui/material/ListItem";
import { enqueueSnackbar } from "notistack";
import { useGetFeeScheduleCategoriesQuery } from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import Pagination from "@mui/material/Pagination";
import Loader from "_shared/_views/loading/loader";
import FeeScheduleCategorySchema from "../fee-schedule-category-schema";
import { withoutFeeScheduleTeamMember } from "../fee-schedule-template-category";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  paddingBottom: theme.spacing(8),
}));
const ListContainer = styled(List)(({ theme }) => ({
  flex: 1,
  maxWidth: theme.spacing(50),
  minHeight: 0,
  overflowY: "auto",
}));
const ButtonsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
}));
const PaginationControls = styled(Pagination)(({ theme }) => ({
  '&.MuiPagination-root > ul': {
    justifyContent: 'space-evenly'
  },
  flexShrink: 0,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: '100%'
}));
const AddSelectedButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  marginTop: theme.spacing(2),
}));
// #endregion

// #region Props/Types
type FeeScheduleCategoryListProps = {
  disabledCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void;
  onCancel: () => void;
  /** When browsing from a proposal's fee-schedule editor, scopes the catalog read to that proposal's grant (need-to-know). */
  proposalId?: string;
};

export default function FeeScheduleCategoryList({
  disabledCategories,
  onCategoriesSelected,
  onCancel,
  proposalId,
}: Readonly<FeeScheduleCategoryListProps>) {
  const pageSize = 25;
  const debounceDelay = 300;

  // #region State
  const [rawSelectedIds, setRawSelectedIds] = useState<Set<string>>(new Set());
  const [selectedCategoriesById, setSelectedCategoriesById] = useState<Map<string, FeeScheduleCategory>>(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const disabledNames = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) {
      return new Set<string>();
    }
    return new Set(
      disabledCategories
        .map(getCategoryName)
        .map(normalizeCategoryName)
        .filter((name): name is string => Boolean(name))
    );
  }, [disabledCategories]);

  const disabledIds = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) return new Set<string>();
    return new Set(disabledCategories.map(getCategoryId).filter((id): id is string => Boolean(id)));
  }, [disabledCategories]);

  const selectedIds = useMemo(() => {
    return new Set([...rawSelectedIds].filter((id) => !disabledIds.has(id)));
  }, [rawSelectedIds, disabledIds]);

  const canSubmit = useMemo(
    () => selectedIds.size > 0,
    [selectedIds]
  );
  // #region Hooks
  const categoriesAPIQuery = useGetFeeScheduleCategoriesQuery({
    query: debouncedSearchTerm,
    pageIndex: page - 1,
    pageSize,
    proposalId,
  });

  const availableCategories = useMemo(() => {
    return categoriesAPIQuery.data?.data
      .map((categoryJSON) => FeeScheduleCategory.fromJSON(FeeScheduleCategorySchema.validateJSON(categoryJSON)))
      ?? [];
  }, [categoriesAPIQuery.data]);

  const displayedCategories = useMemo(() => {
    if (Masked.isMasked(disabledCategories)) return availableCategories;
    const displayedById = new Map<string, FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>(
      availableCategories.map((category) => [getCategoryId(category), category]),
    );
    for (const selectedCategory of disabledCategories) {
      const id = getCategoryId(selectedCategory);
      if (id && !displayedById.has(id)) displayedById.set(id, selectedCategory);
    }
    return [...displayedById.values()];
  }, [availableCategories, disabledCategories]);

  useEffect(() => {
    const timeoutId = globalThis.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, debounceDelay);

    return () => globalThis.clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (categoriesAPIQuery.isError) {
      enqueueSnackbar("Error loading fee schedule categories", { variant: "error" });
    }
  }, [categoriesAPIQuery.isError]);

  // #region Functions
  const toggleCategorySelection = useCallback((category: FeeScheduleCategory) => {
    const categoryId = category.id.toString();
    setRawSelectedIds((prev) => {
      const next = new Set(prev);

      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }

      return next;
    });
    setSelectedCategoriesById((prev) => {
      const next = new Map(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.set(categoryId, category);
      }
      return next;
    });
  }, []);

  const handleCategoryClick = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => {
      if (isMaskedCategory(category)) return;
      const unmaskedCategory = category as FeeScheduleCategory;
      toggleCategorySelection(unmaskedCategory);
    },
    [toggleCategorySelection]
  );

  const handleAddSelectedClicked = useCallback(() => {
    const selectedCategories = [...selectedCategoriesById.values()].filter((category) =>
      selectedIds.has(category.id.toString()) && !disabledNames.has(normalizeCategoryName(category.name.value))
    );

    if (selectedCategories.length === 0) return;

    onCategoriesSelected(selectedCategories.map(withoutFeeScheduleTeamMember));
  }, [disabledNames, onCategoriesSelected, selectedCategoriesById, selectedIds]);
  // #endregion

  // #region Render
  if (categoriesAPIQuery.isLoading) return <Loader />;

  return (
    <MainContainer>
      <TextField
        variant="outlined"
        fullWidth
        placeholder="Search Categories"
        disabled={!availableCategories?.length && !searchTerm.length}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm.length > 0 && (
              <Tooltip title="Clear search">
                <IconButton onClick={() => setSearchTerm("")} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ),
          }
        }}
      />
      <ListContainer>
        {displayedCategories.length === 0 ? (
          <ListItem disableGutters>
            <ListItemText primary="No existing fee schedule categories" />
          </ListItem>
        ) : (
          displayedCategories.map((category) => {
            const categoryName = category.name.value;
            const categoryId = category.id.toString();
            const isAlreadySelected = disabledNames.has(normalizeCategoryName(categoryName));
            const isRestricted = Masked.isMasked(disabledCategories);
            const isDisabled = isAlreadySelected || isRestricted;
            const isChecked = isDisabled || selectedIds.has(categoryId);

            return (
              <ListItemButton
                key={categoryId}
                alignItems="flex-start"
                disableGutters
                disabled={isDisabled}
                onClick={() => handleCategoryClick(category)}
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={isChecked}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{ whiteSpace: "pre-line" }}
                  primary={categoryName}
                  secondary={isAlreadySelected
                      ? "Already selected in this fee schedule. Choose another category."
                    : isRestricted
                      ? "Unavailable because this fee schedule is restricted."
                      : category.feeString}
                />
              </ListItemButton>
            );
          })
        )}
      </ListContainer>
      <PaginationControls 
        count={categoriesAPIQuery.data?.headers.totalPages ?? 0}
        page={page}
        disabled={(categoriesAPIQuery.data?.headers.totalPages ?? 0) <= 1}
        onChange={(_event, value) => setPage(value)}
      />
      <ButtonsContainer>
        <AddSelectedButton
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          startIcon={<AddIcon />}
          onClick={handleAddSelectedClicked}
        >
          Add Selected
        </AddSelectedButton>
        <Button
          color="primary"
          variant="outlined"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </ButtonsContainer>
    </MainContainer>
  );
  // #endregion
}

// #region Helper Functions
function isMaskedCategory(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
): category is FeeScheduleCategoryMaskedCurrency {
  return category instanceof FeeScheduleCategoryMaskedCurrency;
}

function getCategoryName(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.name?.value ?? "";
}

function getCategoryId(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  return category.id?.toString() ?? "";
}

function normalizeCategoryName(name: string): string {
  return name.trim().toLowerCase();
}
